// src/components/TradeConfirm/TradeConfirm.jsx
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiAlertTriangle, FiInfo, FiSend, FiShield, FiClock, FiStar, FiUsers, FiFileText, FiCheckCircle } from 'react-icons/fi';
import './TradeConfirm.css';

import { apiFetch } from '../../services/apiClient';

const CHAIN_COLORS = {
  Bitcoin: '#f7931a', Ethereum: '#627eea', BNB: '#f0b90b', BSC: '#f0b90b',
  Solana: '#9945ff', Litecoin: '#bfbbbb', TRON: '#ef0027',
};

const AVATAR_COLORS = ['#3b82f6','#16a34a','#ef4444','#8b5cf6','#f59e0b','#0ea5e9','#22c55e','#ec4899'];
function getAvatarColor(name) {
  return AVATAR_COLORS[(name?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];
}

export default function TradeConfirm() {
  const navigate     = useNavigate();
  const location     = useLocation();
  const offer        = location.state?.offer;

  const [amount, setAmount]   = useState(offer?.amount?.toString() ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  if (!offer) { navigate('/'); return null; }

  const spread      = parseFloat(offer.spread) || 0;
  const spreadColor = spread > 0 ? '#ef4444' : spread < 0 ? '#16a34a' : '#8a9bb0';
  const spreadLabel = spread === 0 ? 'TRM Mercado' : spread > 0 ? `+${spread}%` : `${spread}%`;
  const isBuying    = offer.type === 'sell';
  const action      = isBuying ? 'Comprar' : 'Vender';
  const actionColor = isBuying ? '#3b82f6' : '#16a34a';
  const chainColor  = CHAIN_COLORS[offer.blockchain] ?? '#16a34a';

  const handleConfirm = async () => {
    if (!offer.sellerId) { setError('Esta es una oferta de demostración. Operá con traders reales verificados desde el marketplace.'); return; }
    if (!amount || parseFloat(amount) <= 0) { setError('Ingresa un monto válido'); return; }
    if (parseFloat(amount) > offer.amount) { setError(`El monto no puede superar USDT ${Number(offer.amount).toLocaleString()}`); return; }
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch('/api/trades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(amount), crypto: offer.currency, price: offer.amount,
          spread: offer.spread, blockchain: offer.blockchain, payment: offer.payment,
          sellerId: offer.sellerId,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Error al crear el trade'); }
      const trade = await res.json();
      navigate(`/trades/${trade.id}`);
    } catch (err) { setError(err.message); setLoading(false); }
  };

  const pct = (p) => Math.floor(offer.amount * p / 100);

  return (
    <div className="tconf-wrapper">
      <div className="tconf-card">

        {/* Colored top accent */}
        <div className="tconf-accent" style={{ background: actionColor }} />

        {/* Header */}
        <div className="tconf-header">
          <button className="tconf-back" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Volver
          </button>
          <div className="tconf-title-row">
            <h2 className="tconf-title">
              <span className="tconf-action-badge" style={{ background: actionColor }}>{action}</span>
              {offer.currency}
            </h2>
            <div className="fin-chip tconf-chain-pill" style={{ borderColor: chainColor + '40', color: chainColor }}>
              <span className="tconf-chain-dot" style={{ background: chainColor }} />
              {offer.blockchain}
            </div>
          </div>
        </div>

        {/* Trader card */}
        <div className="tconf-trader">
          <div className="tconf-trader-left">
            <div className="tconf-avatar" style={{ background: getAvatarColor(offer.trader), borderColor: actionColor }}>
              {offer.trader.slice(0, 2).toUpperCase()}
            </div>
            <div className="tconf-trader-info">
              <span className="tconf-trader-name">@{offer.trader}</span>
              <div className="tconf-trader-stats">
                <span><FiUsers className="tconf-mini-icon" /> <span className="fin-num">{offer.trades}</span> trades</span>
                <span><FiStar className="tconf-mini-icon" /> <span className="fin-num">{offer.rating}</span></span>
              </div>
            </div>
          </div>
          <div className="tconf-resp" title="Tiempo de respuesta">
            <FiClock className="tconf-resp-icon" />
            <span className="fin-num">{offer.responseTime} min</span>
          </div>
        </div>

        {/* Details grid */}
        <div className="tconf-details">
          <div className="tconf-detail">
            <span className="tconf-detail-label fin-label">Método de pago</span>
            <span className="tconf-detail-value"><span className="fin-chip">{offer.payment}</span></span>
          </div>
          <div className="tconf-detail">
            <span className="tconf-detail-label fin-label">Precio</span>
            <span className="tconf-detail-value fin-num" style={{ color: spreadColor, fontWeight: 700 }}>{spreadLabel}</span>
          </div>
          <div className="tconf-detail">
            <span className="tconf-detail-label fin-label">Disponible</span>
            <span className="tconf-detail-value fin-num">USDT {Number(offer.amount).toLocaleString()}</span>
          </div>
          {offer.minAmount > 0 && (
            <div className="tconf-detail">
              <span className="tconf-detail-label fin-label">Mínimo</span>
              <span className="tconf-detail-value fin-num">USDT {Number(offer.minAmount).toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Amount input */}
        <div className="tconf-amount-section">
          <label className="tconf-amount-label">
            ¿Cuánto quieres {isBuying ? 'comprar' : 'vender'}?
          </label>
          <div className="tconf-amount-wrap">
            <span className="tconf-amount-prefix fin-num">USDT</span>
            <input
              type="number"
              className="tconf-amount-input fin-num"
              value={amount}
              onChange={e => { setAmount(e.target.value); setError(''); }}
              placeholder="0"
              min="1"
              max={offer.amount}
            />
          </div>

          {/* Quick amounts */}
          <div className="tconf-quick">
            {[25, 50, 75, 100].map(p => (
              <button key={p} className={`tconf-quick-btn fin-num ${amount === pct(p).toString() ? 'active' : ''}`} onClick={() => setAmount(pct(p).toString())}>
                {p}%
              </button>
            ))}
            <button className={`tconf-quick-btn tconf-quick-max ${amount === offer.amount.toString() ? 'active' : ''}`} onClick={() => setAmount(offer.amount.toString())}>
              Máx
            </button>
          </div>
        </div>

        {error && (
          <div className="tconf-error">
            <FiAlertTriangle className="tconf-error-icon" /> {error}
          </div>
        )}

        {/* Qué va a pasar */}
        <div className="tconf-steps">
          <div className="tconf-steps-title"><FiInfo className="tconf-steps-ic" /> Qué va a pasar</div>
          <ol className="tconf-steps-list">
            <li><strong>Confirmás</strong> y se abre el trade con chat en vivo</li>
            <li><strong>Pagás</strong> por {offer.payment} y subís el comprobante</li>
            <li>El vendedor verifica el pago en su cuenta</li>
            <li>Libera tu cripto — suele responder en <strong>~{offer.responseTime} min</strong></li>
          </ol>
        </div>

        {/* Términos del vendedor */}
        <div className="tconf-terms">
          <div className="tconf-terms-title"><FiFileText className="tconf-steps-ic" /> Términos del vendedor</div>
          <div className="tconf-terms-grid">
            <div className="tconf-term"><span className="fin-label">Ventana de pago</span><strong><FiClock className="tconf-term-ic" /> 30 min</strong></div>
            <div className="tconf-term"><span className="fin-label">Método aceptado</span><strong>{offer.payment}</strong></div>
            <div className="tconf-term"><span className="fin-label">Requisito</span><strong><FiCheckCircle className="tconf-term-ic" /> Cuenta verificada</strong></div>
            <div className="tconf-term"><span className="fin-label">Disponibilidad</span><strong>Responde ~{offer.responseTime} min</strong></div>
          </div>
          <p className="tconf-terms-note">“Subí el comprobante con fecha y hora visibles. No marques como pagado si no transferiste. Coordinamos todo por el chat — gracias por operar con confianza.”</p>
        </div>

        {/* Info box */}
        <div className="tconf-info">
          <FiShield className="tconf-info-icon" />
          <p>Operás con un <strong>vendedor verificado</strong> por invitación. Tenés <strong>30 min</strong> para subir el comprobante; podés coordinar todo por el chat de forma segura.</p>
        </div>

        {/* Actions */}
        <div className="tconf-actions">
          <button className="tconf-btn-cancel" onClick={() => navigate(-1)}>
            Cancelar
          </button>
          <button
            className="tconf-btn-confirm"
            style={{ background: `linear-gradient(135deg, ${actionColor}, ${actionColor}dd)` }}
            onClick={handleConfirm}
            disabled={loading || !amount}
          >
            {loading ? (
              <span className="tconf-spinner" />
            ) : (
              <><FiSend className="tconf-btn-icon" /> Confirmar {action}</>
            )}
          </button>
        </div>

      </div>
    </div>
  );
}
